import { useState } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { NotificationManager } from 'react-notifications';
import axios from 'axios';
import { ManageResult, SendOutcome } from '../../services/service-evaluations';
import { EvaluationErrors } from '../../util/evaluationContract';
import { evaluationErrorMessage, OutcomeMail, outcomeToast } from '../app-evaluation-start';
import { reportUnexpected } from './reportUnexpected';

export type ManageHandlers = {
  onResult: (result: ManageResult) => void;
  onStale: () => void;
};

const isClean = (outcome: SendOutcome) => !outcome.failed.length && !outcome.skipped.length;

const showOutcome = (t: TFunction, outcome: SendOutcome, mail: OutcomeMail = 'invitation') => {
  const toast = outcomeToast(t, outcome, mail);
  const kind = toast.kind === 'success' && outcome.sent === 0 ? 'warning' : toast.kind;
  NotificationManager[kind](toast.message);
};

// A send toast says success only when something was actually sent.
export const notifySend = (t: TFunction, outcome: SendOutcome, successMessage?: string) => {
  if (successMessage && outcome.sent > 0 && isClean(outcome)) {
    NotificationManager.success(successMessage);
    return;
  }
  showOutcome(t, outcome);
};

export const notifyDone = (
  t: TFunction,
  message: string,
  outcome: SendOutcome | null,
  mail?: OutcomeMail,
) => {
  NotificationManager.success(message);
  if (outcome && !isClean(outcome)) showOutcome(t, outcome, mail);
};

// Safari copies only inside the click, so the write must start before the text arrives.
export const copyText = (text: Promise<string>): Promise<void> => {
  if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) {
    return text.then(value => navigator.clipboard.writeText(value));
  }
  const blob = text.then(value => new Blob([value], { type: 'text/plain' }));
  return navigator.clipboard.write([new ClipboardItem({ 'text/plain': blob })]);
};

const STALE_STATUSES = [403, 404, 409, 410];

const STATE_CHANGED: string[] = [
  EvaluationErrors.Closed,
  EvaluationErrors.AlreadyAnswered,
  EvaluationErrors.NotFound,
  EvaluationErrors.RequestNotClosed,
];

export const errorCodeOf = (error: unknown): string | undefined => {
  if (!axios.isAxiosError(error)) return undefined;
  const code = (error.response?.data as { error?: unknown } | undefined)?.error;
  return typeof code === 'string' ? code : undefined;
};

const isStateChanged = (error: unknown) => STATE_CHANGED.includes(errorCodeOf(error) ?? '');

export const reportActionError = (t: TFunction, error: unknown, onStale: () => void) => {
  reportUnexpected(error);
  const message = evaluationErrorMessage(t, error);
  if (message) NotificationManager.error(message);
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  if (status !== undefined && STALE_STATUSES.includes(status)) onStale();
};

type ModalOptions = {
  closeOnStateChange?: () => void;
  showsOwnRefusal?: (error: unknown) => boolean;
};

export const useManageAction = (
  { onResult, onStale }: ManageHandlers,
  { closeOnStateChange, showsOwnRefusal }: ModalOptions = {},
) => {
  const [t] = useTranslation('evaluations');
  const [saving, setSaving] = useState(false);

  const run = async <Result extends ManageResult>(
    call: () => Promise<Result>,
    onDone: (result: Result) => void,
  ) => {
    if (saving) return;
    setSaving(true);
    try {
      const result = await call();
      onResult(result);
      onDone(result);
    } catch (error) {
      if (showsOwnRefusal?.(error)) return;
      reportActionError(t, error, onStale);
      if (closeOnStateChange && isStateChanged(error)) closeOnStateChange();
    } finally {
      setSaving(false);
    }
  };

  return { saving, run };
};
